/**
 * Court geometry for the live stage. Pure, no React.
 *
 * Feed coordinates (verified against every committed fixture shot): `x` runs along the
 * court LENGTH (0–100 → 0–94 ft), `y` across the WIDTH (0–100 → 0–50 ft). Baskets sit
 * 5.25 ft from each baseline (x ≈ 5.6 and ≈ 94.4). Teams switch baskets at half-time,
 * exactly as in the feed; we draw shots where they really happened.
 */
import type { GameEvent, Side } from '@clutch/core';

export const COURT = {
  length: 94,
  width: 50,
  rimFromBaseline: 5.25,
  rimRadius: 0.75,
  backboardFromBaseline: 4,
  threeRadius: 23.75,
  cornerThreeFromCenter: 22, // corner 3 line is 22 ft from the rim, 3 ft in from the sideline
  cornerLength: 14, // straight corner segment, measured from the baseline
  laneWidth: 16,
  laneLength: 19, // baseline → free-throw line
  ftCircleRadius: 6,
  restrictedRadius: 4,
  centerCircleRadius: 6,
} as const;

export type Pt = { x: number; y: number }; // feet; x along length, y across width

/** Feed units (0–100 on both axes) → court feet. */
export function feedToCourt(x: number, y: number): Pt {
  return { x: (x / 100) * COURT.length, y: (y / 100) * COURT.width };
}

export function hasCoords(e: Pick<GameEvent, 'x' | 'y'>): e is { x: number; y: number } {
  return typeof e.x === 'number' && Number.isFinite(e.x) && typeof e.y === 'number' && Number.isFinite(e.y);
}

/** The basket nearest a court point (the one being attacked). */
export function hoopFor(p: Pt): Pt {
  const left = p.x < COURT.length / 2;
  return { x: left ? COURT.rimFromBaseline : COURT.length - COURT.rimFromBaseline, y: COURT.width / 2 };
}

export function shotDistanceFt(p: Pt): number {
  const h = hoopFor(p);
  return Math.hypot(p.x - h.x, p.y - h.y);
}

export function insideCourt(p: Pt): boolean {
  return p.x >= 0 && p.x <= COURT.length && p.y >= 0 && p.y <= COURT.width;
}

/**
 * Is a court point beyond the three-point line? Corners (within 14 ft of the
 * baseline) use the 22 ft straight line; elsewhere the 23.75 ft arc. `tol` in feet
 * absorbs the feed's coordinate rounding.
 */
export function isBeyondArc(p: Pt, tol = 0): boolean {
  const h = hoopFor(p);
  const fromBaseline = p.x < COURT.length / 2 ? p.x : COURT.length - p.x;
  if (fromBaseline <= COURT.cornerLength && Math.abs(p.y - h.y) >= COURT.cornerThreeFromCenter - tol) return true;
  return Math.hypot(p.x - h.x, p.y - h.y) >= COURT.threeRadius - tol;
}

/** Which basket (by court end) did `side` attack in `period`, from that period's shots so far? */
export function attackingEnd(events: readonly GameEvent[], side: Side, period: number): 'left' | 'right' | undefined {
  let l = 0;
  let r = 0;
  for (const e of events) {
    if (e.period !== period || e.side !== side || e.type !== 'shot' || !hasCoords(e)) continue;
    if (e.x < 50) l++;
    else r++;
  }
  if (l === r) return undefined;
  return l > r ? 'left' : 'right';
}

// ───────────── Captions ─────────────

export type PlayCaption = {
  text: string; // "BRUNSON · 29 PTS · 3PT"
  side?: Side;
  points?: number; // points scored on the play (for "+3")
  tone: 'make' | 'miss' | 'info';
};

const last = (e: Pick<GameEvent, 'playerName'>) => (e.playerName ?? '').toUpperCase();

/** "3PT" | "DUNK" | "LAYUP" | "JUMPER" | "HOOK" | "2PT" — from points and the feed description. */
export function shotKind(e: Pick<GameEvent, 'points' | 'description'>): string {
  const d = e.description;
  if (e.points === 3 || /\b3PT\b/i.test(d)) return '3PT';
  if (/dunk/i.test(d)) return 'DUNK';
  if (/layup/i.test(d)) return 'LAYUP';
  if (/hook/i.test(d)) return 'HOOK';
  if (/jump shot|jumper|fadeaway|pullup|step back|shot/i.test(d)) return 'JUMPER';
  return '2PT';
}

/**
 * The on-stage caption for a play, or undefined if it's not caption-worthy.
 * Only fields from the event itself — nothing inferred.
 */
export function playCaption(e: GameEvent, teamName: (s: Side) => string): PlayCaption | undefined {
  // ESPN sometimes has the scorer's name only in its play description.
  const who = last(e) || ((e.type === 'shot' || e.type === 'freethrow') ? /^(.*?)\s+(?:makes|misses)\s/i.exec(e.description)?.[1]?.toUpperCase() ?? '' : '');
  switch (e.type) {
    case 'shot': {
      const kind = shotKind(e);
      if (e.made) {
        const parts = [who || 'BASKET'];
        if (typeof e.playerPoints === 'number') parts.push(`${e.playerPoints} PTS`);
        parts.push(kind);
        return { text: parts.join(' · '), side: e.side, points: e.points, tone: 'make' };
      }
      return { text: `${who || 'SHOT'} · MISSES ${kind}`, side: e.side, tone: 'miss' };
    }
    case 'freethrow': {
      const m = /(\d) of (\d)/.exec(e.description);
      const ft = m ? `FT ${m[1]}/${m[2]}` : 'FT';
      if (e.made) {
        const parts = [who || 'FREE THROW', ft];
        if (typeof e.playerPoints === 'number') parts.push(`${e.playerPoints} PTS`);
        return { text: parts.join(' · '), side: e.side, points: e.points, tone: 'make' };
      }
      return { text: `${who || 'FREE THROW'} · MISSES ${ft}`, side: e.side, tone: 'miss' };
    }
    case 'turnover':
      return { text: `TURNOVER · ${e.side ? teamName(e.side).toUpperCase() : who}`, side: e.side, tone: 'info' };
    case 'steal':
      return { text: `${who} · STEAL`, side: e.side, tone: 'info' };
    case 'block':
      return { text: `${who} · BLOCK`, side: e.side, tone: 'info' };
    case 'foul':
      return { text: `FOUL · ${who || (e.side ? teamName(e.side).toUpperCase() : '')}`, side: e.side, tone: 'info' };
    case 'timeout':
      return { text: `TIMEOUT · ${e.side ? teamName(e.side).toUpperCase() : 'OFFICIAL'}`, side: e.side, tone: 'info' };
    case 'period_end':
      return { text: e.period <= 4 ? `END OF Q${e.period}` : 'END OF OT', tone: 'info' };
    case 'period_start':
      return { text: e.period <= 4 ? `START OF Q${e.period}` : 'START OF OT', tone: 'info' };
    case 'game_end':
      return { text: 'FINAL', tone: 'info' };
    default:
      return undefined;
  }
}

/** Event types the stage keeps in its play log (others never render). */
export const STAGE_TYPES: ReadonlySet<GameEvent['type']> = new Set([
  'shot',
  'freethrow',
  'turnover',
  'steal',
  'block',
  'foul',
  'timeout',
  'period_start',
  'period_end',
  'game_end',
]);

/** Merge plays by seq (dedupe), keep only ≤ asOfSeq (never show anything the server hasn't applied). */
export function mergePlays(a: readonly GameEvent[], b: readonly GameEvent[], asOfSeq: number): GameEvent[] {
  const bySeq = new Map<number, GameEvent>();
  for (const e of a) if (e.seq <= asOfSeq && STAGE_TYPES.has(e.type)) bySeq.set(e.seq, e);
  for (const e of b) if (e.seq <= asOfSeq && STAGE_TYPES.has(e.type)) bySeq.set(e.seq, e);
  return [...bySeq.values()].sort((x, y) => x.seq - y.seq);
}
